import { useState, useEffect } from 'react';

import {
  map as _map,
  includes as _includes,
  uniqBy as _uniqBy,
  isFunction as _isFunction,
} from 'lodash';

import { PRODUCT_STATUS } from '../../Services/Enums';

const getCategories = (items, emptyCategoryFlag, withUnusedLicences, customCheckUnusedLicences) => {
  let categoryList = [];
  const otherCategoryId = 'core_soho_addon_categories_other_id';
  const unusedCategoryId = 'core_soho_unused_categories_id';
  const isUnusedLicences = _isFunction(customCheckUnusedLicences)
    ? customCheckUnusedLicences(items)
    : checkUnusedLicences(items);

  _map(items, item => {
    categoryList = [...categoryList, ...item.category];
  });

  if (emptyCategoryFlag) {
    categoryList.push({
      id: otherCategoryId,
      name: 'soho_addons_shop_addons_other_filter',
    });
  }

  if (isUnusedLicences && withUnusedLicences) {
    categoryList.unshift({
      id: unusedCategoryId,
      name: 'soho_licences_filter_unused_licences',
    });
  }

  categoryList = _uniqBy(categoryList, 'id');
  return categoryList;
};

const filterByCategories = (_selectedCategories, _productItems, customCheckUnusedLicences) => {
  const OtherCategoryId = 'core_soho_addon_categories_other_id';
  const unusedCategoryId = 'core_soho_unused_categories_id';
  return _productItems.filter(product => {
    const { category } = product;

    //get all categories for each application
    let catIds = category.map(({ id }) => id);

    // check if other filter is selected
    if (category.length === 0 && _includes(_selectedCategories, OtherCategoryId)) {
      catIds.push(OtherCategoryId);
    }
    // check if the unused filter is selected
    const _checkUnusedLicences = _isFunction(customCheckUnusedLicences)
      ? customCheckUnusedLicences
      : checkUnusedLicences;

    if (_includes(_selectedCategories, unusedCategoryId) && _checkUnusedLicences([product])) {
      catIds.push(unusedCategoryId);
    }
    // check the categories of the app are found on selected filters
    catIds = catIds.filter(c => _includes(_selectedCategories, c));
    return catIds.length !== 0;
  });
};

const getMatchedCategories = (searchedCategories, filteredCategories) => {
  const matched = [];
  filteredCategories.forEach(filteredCategory => {
    searchedCategories.forEach(searchedCategory => {
      if (searchedCategory.id === filteredCategory.id) {
        matched.push(searchedCategory);
      }
    });
  });
  return matched;
};

const filterProducts = (
  _selectedCategories,
  _productItems,
  _setProducts,
  customCheckUnusedLicences,
) => {
  const isSelectedCategoryPressed = _selectedCategories?.length ? true : false;
  let filteredProducts = _productItems;

  if (isSelectedCategoryPressed) {
    filteredProducts = filterByCategories(
      _selectedCategories,
      _productItems,
      customCheckUnusedLicences,
    );
  }
  _setProducts(filteredProducts);
};
const handelSetSelectedCategories = (categoryId, _selectedCategories, _setSelectedCategories) => {
  if (!categoryId) {
    _setSelectedCategories([]);
    return;
  }
  if (!_includes(_selectedCategories, categoryId)) {
    _setSelectedCategories([..._selectedCategories, categoryId]);
  } else {
    const filteredCats = _selectedCategories.filter(cat => {
      return cat !== categoryId;
    });
    _setSelectedCategories(filteredCats);
  }
};

const checkUnusedLicences = (applications = []) => {
  let usedLicencesCount = 0;

  for (const { licences } of applications) {
    if (!licences) {
      return false;
    }
    usedLicencesCount = 0;
    for (const { status } of licences) {
      if (status === PRODUCT_STATUS.ACTIVE) {
        usedLicencesCount++;
      }
    }
    if (usedLicencesCount !== licences.length) {
      return true;
    }
  }
  return false;
};

function useMatchedFilterSearchedProducts(filteredProducts, searchObj) {
  const [matchedProducts, setMatchedProducts] = useState(filteredProducts);
  useEffect(() => {
    const isSearchingByWord = searchObj?.searchWord?.length !== 0;
    if (isSearchingByWord) {
      setMatchedProducts(getMatchedCategories(searchObj.searchedData, filteredProducts));
    } else {
      setMatchedProducts(filteredProducts);
    }
  }, [searchObj, filteredProducts]);

  return {
    matchedProducts,
  };
}

function useProductsCategoriesFilter(
  productItems,
  customCheckUnusedLicences,
  withUnusedLicences = false,
) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    let emptyCategoryFlag = 0;
    _map(productItems, item => {
      emptyCategoryFlag = item?.category?.length ? emptyCategoryFlag : ++emptyCategoryFlag;
    });

    const categoryList = getCategories(
      productItems,
      emptyCategoryFlag,
      withUnusedLicences,
      customCheckUnusedLicences,
    );
    setCategories(categoryList);
    setProducts(productItems);
  }, [productItems]);

  useEffect(() => {
    filterProducts(selectedCategories, productItems, setProducts, customCheckUnusedLicences);
  }, [selectedCategories]);

  const onSelectedCategoriesChange = categoryId => {
    handelSetSelectedCategories(categoryId, selectedCategories, setSelectedCategories);
  };

  return {
    products,
    selectedCategories,
    categories,
    setProducts,
    onSelectedCategoriesChange,
    setSelectedCategories,
  };
}

export {
  useProductsCategoriesFilter,
  getCategories,
  filterProducts,
  handelSetSelectedCategories,
  checkUnusedLicences,
  useMatchedFilterSearchedProducts,
};
